/* eslint-disable react/display-name */
import {
	Box,
	FormControl,
	FormHelperText,
	InputAdornment,
	TextField,
	TextFieldProps,
} from '@mui/material'
import { ChangeEvent, forwardRef, ReactNode } from 'react'
import { FieldError, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type InputProps = TextFieldProps & {
	unit?: string
	width?: 'half' | 'full'
	fieldError?: FieldError
	name?: string
	endAdornment?: ReactNode
	validateOnBlur?: boolean
}

const Input = forwardRef((props: InputProps, ref) => {
	const zeroPadding = { padding: 0 }
	const { t } = useTranslation()
	const {
		unit,
		onChange,
		fieldError,
		name,
		value,
		type,
		variant,
		endAdornment,
		validateOnBlur,
		...rest
	} = props
	const { trigger } = useFormContext()

	const onChangeAndRevalidate = (
		e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
	) => {
		onChange && onChange(e)
		if (!validateOnBlur) {
			trigger(name)
		}
	}

	const onBlurHandler = () => {
		if (validateOnBlur) {
			trigger(name)
		}
	}

	const messageRenderer = () => {
		switch (fieldError?.type) {
			case 'required':
				return t('input.required')
			case 'minLength':
				return t('input.minLength')
			case 'maxLength':
				return t('input.maxLength')
			case 'pattern':
			case 'validate':
				return fieldError?.message
			default:
				return null
		}
	}

	return (
		<Box ref={ref}>
			<FormControl required={rest.required} fullWidth>
				<TextField
					variant={variant}
					onChange={onChangeAndRevalidate}
					error={!!fieldError?.type}
					sx={zeroPadding}
					InputProps={{
						endAdornment: unit ? (
							<InputAdornment position="end">{unit}</InputAdornment>
						) : endAdornment ? (
							endAdornment
						) : (
							<></>
						),
					}}
					InputLabelProps={{
						shrink: true,
					}}
					value={
						type === 'number' && value
							? (value as string | number).toString()
							: value
					}
					type={type}
					onBlur={onBlurHandler}
					{...rest}
				/>
				<Box height={6}>
					<FormHelperText error={true}>{messageRenderer()}</FormHelperText>
				</Box>
			</FormControl>
		</Box>
	)
})

export { Input }
